/**
 * Brand Assets picker (PlayAd "Brand Assets" modal): the asset library with multi-select and a
 * "Use N assets" button. Used by the chat composer "+" menu and Studio (reference images).
 * Data: see AssetLibrary (assets.list / assets.update / assets.upload).
 */
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Overlay";
import { useTopmostClose } from "../creative/hooks";
import { AssetImportModal } from "./AssetImportModal";
import { AssetLibrary } from "./AssetLibrary";
import type { BrandAsset } from "./normalize";

export interface PickedAsset {
  id: string;
  url: string;
  name?: string;
  kind?: string;
  mediaType?: "image" | "video" | "font";
}

export interface AssetPickerProps {
  brandId: string;
  open: boolean;
  onClose: () => void;
  onPick: (assets: PickedAsset[]) => void;
  multiple?: boolean;
  /** Restrict to kinds (logo, icon, product, screenshot, lifestyle, ugc, font, other). */
  kinds?: string[];
  title?: string;
}

export function toPickedAsset(a: BrandAsset): PickedAsset {
  return { id: a.id, url: a.url, name: a.name || undefined, kind: a.kind, mediaType: a.mediaType };
}

export function AssetPicker({ brandId, open, onClose, onPick, multiple = true, kinds, title = "Brand Assets" }: AssetPickerProps) {
  const { marker, close } = useTopmostClose(onClose);
  const [selected, setSelected] = useState<BrandAsset[]>([]);
  const [importing, setImporting] = useState(false);
  useEffect(() => {
    if (open) setSelected([]);
  }, [open, brandId]);
  const toggle = (a: BrandAsset) => setSelected((prev) => (prev.some((x) => x.id === a.id) ? prev.filter((x) => x.id !== a.id) : multiple ? [...prev, a] : [a]));
  const n = selected.length;
  return (
    <>
      <Modal
        open={open}
        onClose={close}
        size="xl"
        className={marker}
        title={title}
        bodyClassName="min-h-[420px]"
        footer={
          <>
            <span className="mr-auto text-[12.5px] text-muted">{n ? `${n} selected` : multiple ? "Select one or more assets" : "Select an asset"}</span>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!n}
              icon={<Check className="size-3.5" />}
              onClick={() => {
                onPick(selected.map(toPickedAsset));
                onClose();
              }}
            >
              {n ? `Use ${n} asset${n > 1 ? "s" : ""}` : "Use assets"}
            </Button>
          </>
        }
      >
        <AssetLibrary
          brandId={brandId}
          mode="pick"
          selected={selected}
          onToggle={toggle}
          kinds={kinds}
          onImport={() => setImporting(true)}
          gridClassName="grid-cols-[repeat(auto-fill,minmax(140px,1fr))]"
        />
      </Modal>
      <AssetImportModal open={open && importing} onClose={() => setImporting(false)} brandId={brandId} />
    </>
  );
}
